import React, { useState, useEffect } from 'react'
import {
  Container,
  Header,
  SpaceBetween,
  Form,
  FormField,
  Input,
  Button,
  Box,
  Alert,
  Tabs,
  Table,
  Badge,
  Modal
} from '@cloudscape-design/components'
import { apiService } from '../services/api'

interface ScrapingTarget {
  id: string
  name: string
  url: string
  selector: string
  status: 'active' | 'inactive' | 'error'
  lastTest: string
}

const Configuration: React.FC = () => {
  const [scrapingTargets, setScrapingTargets] = useState<ScrapingTarget[]>([])
  const [loading, setLoading] = useState(true)
  const [alert, setAlert] = useState<{ type: 'success' | 'error', message: string } | null>(null)
  const [showAddTargetModal, setShowAddTargetModal] = useState(false)
  const [newTarget, setNewTarget] = useState({
    name: '',
    url: '',
    selector: ''
  })

  const [apiConfig, setApiConfig] = useState({
    metarEndpoint: '',
    marineEndpoint: '',
    collectionInterval: '60',
    retryAttempts: '3',
    timeout: '30'
  })

  useEffect(() => {
    loadConfigurations()
  }, [])

  const loadConfigurations = async () => {
    setLoading(true)
    try {
      // Load configurations for all data products
      const dataProducts = ['metar', 'marine', 'trail', 'nws', 'beach_conditions']
      const configPromises = dataProducts.map(product => 
        apiService.getConfiguration(product)
      )
      
      const responses = await Promise.all(configPromises)
      
      // Process METAR config
      const metarConfig = responses[0]
      if (metarConfig.statusCode === 200 && metarConfig.data) {
        setApiConfig(prev => ({
          ...prev,
          metarEndpoint: metarConfig.data.api_endpoint || metarConfig.data.apiEndpoint || '',
          collectionInterval: String(metarConfig.data.collection_interval || metarConfig.data.collectionInterval || 60)
        }))
      }
      
      // Process Marine config
      const marineConfig = responses[1]
      if (marineConfig.statusCode === 200 && marineConfig.data) {
        setApiConfig(prev => ({
          ...prev,
          marineEndpoint: marineConfig.data.api_endpoint || marineConfig.data.apiEndpoint || ''
        }))
      }
      
      // Process Trail config (scraping targets)
      const trailConfig = responses[2]
      if (trailConfig.statusCode === 200 && trailConfig.data) {
        const targets = trailConfig.data.scraping_targets || trailConfig.data.scrapingTargets || []
        const transformedTargets = targets.map((target: any, index: number) => ({
          id: String(index + 1),
          name: target.name,
          url: target.url,
          selector: target.selector,
          status: 'active' as const,
          lastTest: 'Unknown'
        }))
        setScrapingTargets(transformedTargets)
      }
      
    } catch (error) {
      console.error('Error loading configurations:', error)
      setAlert({
        type: 'error',
        message: 'Failed to load configurations. Using default values.'
      })
      
      // Set default values
      setApiConfig({
        metarEndpoint: 'https://aviationweather.gov/api/data/metar',
        marineEndpoint: 'https://seagull.glos.org/api/observations',
        collectionInterval: '60',
        retryAttempts: '3',
        timeout: '30'
      })
      
      setScrapingTargets([
        {
          id: '1',
          name: 'Wisconsin Trail Report',
          url: 'https://www.travelwisconsin.com/snowreport/snowmobile',
          selector: '.trail-condition',
          status: 'active',
          lastTest: 'Unknown'
        },
        {
          id: '2',
          name: 'Fish Web Trails',
          url: 'https://www.fishweb.com/recreation/snowmobile/trails/index.html',
          selector: '.condition-report',
          status: 'active',
          lastTest: 'Unknown'
        }
      ])
    } finally {
      setLoading(false)
    }
  }

  const saveApiConfiguration = async () => {
    try {
      // Save METAR configuration
      const metarResponse = await apiService.updateConfiguration('metar', {
        apiEndpoint: apiConfig.metarEndpoint,
        collectionInterval: parseInt(apiConfig.collectionInterval),
        enabled: true
      })
      
      // Save Marine configuration
      const marineResponse = await apiService.updateConfiguration('marine', {
        apiEndpoint: apiConfig.marineEndpoint,
        collectionInterval: parseInt(apiConfig.collectionInterval),
        enabled: true
      })
      
      if (metarResponse.statusCode === 200 && marineResponse.statusCode === 200) {
        setAlert({
          type: 'success',
          message: 'API configuration saved successfully'
        })
      } else {
        throw new Error('Failed to save configuration')
      }
    } catch (error) {
      console.error('Error saving API configuration:', error)
      setAlert({
        type: 'error',
        message: 'Failed to save API configuration. Please try again.'
      })
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge color="green">Active</Badge>
      case 'inactive':
        return <Badge color="grey">Inactive</Badge>
      case 'error':
        return <Badge color="red">Error</Badge>
      default:
        return <Badge>Unknown</Badge>
    }
  }

  const handleAddTarget = async () => {
    try {
      // Get current trail configuration
      const response = await apiService.getConfiguration('trail')
      let currentTargets = []
      
      if (response.statusCode === 200 && response.data) {
        currentTargets = response.data.scraping_targets || response.data.scrapingTargets || []
      }
      
      // Add new target
      const newTargetData = {
        name: newTarget.name,
        url: newTarget.url,
        selector: newTarget.selector
      }
      
      const updatedTargets = [...currentTargets, newTargetData]
      
      // Update configuration
      const updateResponse = await apiService.updateConfiguration('trail', {
        scrapingTargets: updatedTargets,
        collectionInterval: 60,
        enabled: true
      })
      
      if (updateResponse.statusCode === 200) {
        // Add to local state
        const target: ScrapingTarget = {
          id: String(scrapingTargets.length + 1),
          name: newTarget.name,
          url: newTarget.url,
          selector: newTarget.selector,
          status: 'active',
          lastTest: 'Never'
        }
        setScrapingTargets([...scrapingTargets, target])
        setShowAddTargetModal(false)
        setNewTarget({ name: '', url: '', selector: '' })
        setAlert({
          type: 'success',
          message: 'Scraping target added successfully'
        })
      } else {
        throw new Error('Failed to save scraping target')
      }
    } catch (error) {
      console.error('Error adding scraping target:', error)
      setAlert({
        type: 'error',
        message: 'Failed to add scraping target. Please try again.'
      })
    }
  }

  return (
    <SpaceBetween size="l">
      <Header variant="h1">System Configuration</Header>

      {alert && (
        <Alert
          type={alert.type}
          dismissible
          onDismiss={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}

      <Tabs
        tabs={[
          {
            label: 'API Configuration',
            id: 'api',
            content: (
              <Container header={<Header variant="h2">API Endpoints</Header>}>
                <Form
                  actions={
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button variant="link" onClick={() => loadConfigurations()}>Reset</Button>
                      <Button variant="primary" onClick={saveApiConfiguration}>Save Changes</Button>
                    </SpaceBetween>
                  }
                >
                  <SpaceBetween direction="vertical" size="l">
                    <FormField
                      label="METAR API Endpoint"
                      description="NOAA Aviation Weather API endpoint for METAR data"
                    >
                      <Input
                        value={apiConfig.metarEndpoint}
                        onChange={({ detail }) =>
                          setApiConfig({ ...apiConfig, metarEndpoint: detail.value })
                        }
                      />
                    </FormField>

                    <FormField
                      label="Marine API Endpoint"
                      description="GLOS/Seagull API endpoint for marine observations"
                    >
                      <Input
                        value={apiConfig.marineEndpoint}
                        onChange={({ detail }) =>
                          setApiConfig({ ...apiConfig, marineEndpoint: detail.value })
                        }
                      />
                    </FormField>

                    <FormField
                      label="Collection Interval (minutes)"
                      description="How often to collect data from external sources"
                    >
                      <Input
                        value={apiConfig.collectionInterval}
                        onChange={({ detail }) =>
                          setApiConfig({ ...apiConfig, collectionInterval: detail.value })
                        }
                        type="number"
                      />
                    </FormField>

                    <FormField
                      label="Retry Attempts"
                      description="Number of retry attempts for failed API calls"
                    >
                      <Input
                        value={apiConfig.retryAttempts}
                        onChange={({ detail }) =>
                          setApiConfig({ ...apiConfig, retryAttempts: detail.value })
                        }
                        type="number"
                      />
                    </FormField>

                    <FormField
                      label="Request Timeout (seconds)"
                      description="Timeout for API requests"
                    >
                      <Input
                        value={apiConfig.timeout}
                        onChange={({ detail }) =>
                          setApiConfig({ ...apiConfig, timeout: detail.value })
                        }
                        type="number"
                      />
                    </FormField>
                  </SpaceBetween>
                </Form>
              </Container>
            )
          },
          {
            label: 'Web Scraping',
            id: 'scraping',
            content: (
              <Container
                header={
                  <Header
                    variant="h2"
                    actions={
                      <Button variant="primary" onClick={() => setShowAddTargetModal(true)}>
                        Add Scraping Target
                      </Button>
                    }
                  >
                    Scraping Targets
                  </Header>
                }
              >
                <Table
                  columnDefinitions={[
                    {
                      id: 'name',
                      header: 'Name',
                      cell: item => item.name
                    },
                    {
                      id: 'url',
                      header: 'URL',
                      cell: item => (
                        <div style={{ wordBreak: 'break-all', maxWidth: '300px' }}>
                          {item.url}
                        </div>
                      )
                    },
                    {
                      id: 'selector',
                      header: 'CSS Selector',
                      cell: item => (
                        <code style={{ fontSize: '0.9em' }}>{item.selector}</code>
                      )
                    },
                    {
                      id: 'status',
                      header: 'Status',
                      cell: item => getStatusBadge(item.status)
                    },
                    {
                      id: 'lastTest',
                      header: 'Last Test',
                      cell: item => item.lastTest
                    },
                    {
                      id: 'actions',
                      header: 'Actions',
                      cell: item => (
                        <SpaceBetween direction="horizontal" size="xs">
                          <Button variant="inline-link">Test</Button>
                          <Button variant="inline-link">Edit</Button>
                          <Button variant="inline-link">Delete</Button>
                        </SpaceBetween>
                      )
                    }
                  ]}
                  items={scrapingTargets}
                  loading={loading}
                  empty={
                    <Box textAlign="center" color="inherit">
                      <b>No scraping targets</b>
                      <Box variant="p" color="inherit">
                        No web scraping targets have been configured.
                      </Box>
                    </Box>
                  }
                />
              </Container>
            )
          },
          {
            label: 'System Settings',
            id: 'system',
            content: (
              <SpaceBetween size="l">
                <Container header={<Header variant="h2">General Settings</Header>}>
                  <Alert type="info">
                    System settings are managed through AWS infrastructure configuration.
                    Contact your system administrator for changes to core system settings.
                  </Alert>
                </Container>

                <Container header={<Header variant="h2">Monitoring</Header>}>
                  <Form>
                    <SpaceBetween direction="vertical" size="l">
                      <FormField
                        label="Data Freshness Threshold (minutes)"
                        description="Mark data as 'out of date' after this many minutes"
                      >
                        <Input value="60" type="number" />
                      </FormField>

                      <FormField
                        label="Alert Email"
                        description="Email address for system alerts"
                      >
                        <Input value="admin@live-maps.net" type="email" />
                      </FormField>
                    </SpaceBetween>
                  </Form>
                </Container>
              </SpaceBetween>
            )
          }
        ]}
      />

      <Modal
        visible={showAddTargetModal}
        onDismiss={() => setShowAddTargetModal(false)}
        header="Add Scraping Target"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowAddTargetModal(false)}>
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={handleAddTarget}
                disabled={!newTarget.name || !newTarget.url || !newTarget.selector}
              >
                Add Target
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Form>
          <SpaceBetween direction="vertical" size="l">
            <FormField label="Name" description="A descriptive name for this scraping target">
              <Input
                value={newTarget.name}
                onChange={({ detail }) =>
                  setNewTarget({ ...newTarget, name: detail.value })
                }
                placeholder="e.g., Wisconsin Trail Report"
              />
            </FormField>
            <FormField label="URL" description="The website URL to scrape">
              <Input
                value={newTarget.url}
                onChange={({ detail }) =>
                  setNewTarget({ ...newTarget, url: detail.value })
                }
                placeholder="https://example.com/trail-conditions"
              />
            </FormField>
            <FormField label="CSS Selector" description="CSS selector to extract data from the page">
              <Input
                value={newTarget.selector}
                onChange={({ detail }) =>
                  setNewTarget({ ...newTarget, selector: detail.value })
                }
                placeholder=".trail-condition, #conditions, etc."
              />
            </FormField>
          </SpaceBetween>
        </Form>
      </Modal>
    </SpaceBetween>
  )
}

export default Configuration